import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { trpcPricingListSkusOpen } from '@trashlab/api/src/generated/hooks';
import { useShoppingCart } from '@trashlab/sitesapp/src/hooks/use-shopping-cart';
import {
  IconHome,
  IconBath,
  IconTool,
  IconWall,
  IconBuildingWarehouse,
  IconTruck,
} from '@tabler/icons-react';

const OrderButton = ({
  label,
  skuId,
  productId,
}: {
  label: string;
  skuId: string;
  productId: string;
}) => {
  const { isLoading, addToCart } = useShoppingCart();

  return (
    <button
      onClick={() => addToCart({ skuId, productId })}
      disabled={isLoading}
      className="inline-flex items-center bg-[#E31837] hover:bg-[#C1152E] text-white font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base shadow-md"
    >
      {isLoading ? 'Loading...' : label}
      <span className="ml-2">→</span>
    </button>
  );
};

export const DumpsterSizesIntro = () => {
  const { website } = useTenantContext();
  const tenantId = website?.organization?.id;

  const { data: skus } = trpcPricingListSkusOpen.useQuery(
    {
      tenantId: tenantId ?? '',
      onlineOrderEnabled: true,
      status: 'ACTIVE',
      defaultOnly: true,
    },
    { enabled: !!tenantId },
  );

  const getSku = (yardage: number) =>
    skus?.find((s) => {
      const raw = s.product?.cubicYardage;
      const num =
        typeof raw === 'string'
          ? parseInt(raw.replace(/\D/g, ''), 10) || 0
          : Number(raw) || 0;
      return num === yardage;
    });

  const fiveYdSku = getSku(5);
  const tenYdSku = getSku(10);

  return (
    <>
      {/* 5-Yard Dumpster Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              5-Yard Dumpster (8×4×4)
            </h2>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Dimensions & Capacity
              </h3>
              <p className="text-[#333333] leading-relaxed">
                8 feet long × 4 feet wide × 4 feet tall. Holds approximately 5
                cubic yards of debris — about{' '}
                <strong>2 pickup truck loads</strong>.
              </p>
            </div>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Best For
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: IconHome, text: 'Roofing tear-offs and shingle removal' },
                  { icon: IconBath, text: 'Bathroom remodels — tile, drywall, fixtures, flooring' },
                  { icon: IconTool, text: 'Garage cleanouts and small moves' },
                  { icon: IconWall, text: 'Concrete, asphalt, and brick removal (¾ full – max 3 tons)' },
                  { icon: IconBuildingWarehouse, text: 'Small renovation projects and decluttering' },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <Icon size={22} stroke={1.5} className="text-[#E31837] mt-0.5 shrink-0" />
                    <span className="text-[#333333]">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Fits in Your Driveway
              </h3>
              <p className="text-[#333333] leading-relaxed">
                The 5YD fits in tight spaces, driveways, alleyways, side yards,
                and parking areas. It won&apos;t block access and is lighter than
                a typical roll-off.
              </p>
            </div>

            <div className="text-center">
              {fiveYdSku && fiveYdSku.product?.id && (
                <OrderButton
                  label="Order 5YD Dumpster"
                  skuId={fiveYdSku.id}
                  productId={fiveYdSku.product.id}
                />
              )}
              <p className="text-sm text-gray-500 mt-3">
                Same-day delivery often available · Call{' '}
                <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                  952-931-2577
                </a>{' '}
                with questions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 10-Yard Dumpster Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              10-Yard Dumpster (10×5×5)
            </h2>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Dimensions & Capacity
              </h3>
              <p className="text-[#333333] leading-relaxed">
                10 feet long × 5 feet wide × 5 feet tall. Holds approximately
                10 cubic yards of debris — about{' '}
                <strong>4 pickup truck loads</strong>. The low loading lip
                (roughly 2.5 feet high) makes it easy to toss items in without
                heavy lifting.
              </p>
            </div>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Best For
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: IconBuildingWarehouse, text: 'Kitchen remodels — cabinets, flooring, counters, drywall' },
                  { icon: IconWall, text: 'Room renovations with multiple materials' },
                  { icon: IconTool, text: 'Home or estate cleanouts' },
                  { icon: IconTruck, text: 'Light construction debris — lumber, metal, drywall' },
                  { icon: IconBuildingWarehouse, text: 'Small & medium commercial projects and apartment turns' },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <Icon size={22} stroke={1.5} className="text-[#E31837] mt-0.5 shrink-0" />
                    <span className="text-[#333333]">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-4">
                Fits in Your Driveway
              </h3>
              <p className="text-[#333333] leading-relaxed">
                The 10YD is about the size of a standard parking space. It fits
                in most driveways, side yards, alleyways, and commercial parking
                areas. Same driveway-safe delivery — 2×4s under the bin to
                protect your surface.
              </p>
            </div>

            <div className="text-center">
              {tenYdSku && tenYdSku.product?.id && (
                <OrderButton
                  label="Order 10YD Dumpster"
                  skuId={tenYdSku.id}
                  productId={tenYdSku.product.id}
                />
              )}
              <p className="text-sm text-gray-500 mt-3">
                Same-day delivery often available · Call{' '}
                <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                  952-931-2577
                </a>{' '}
                with questions
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
